import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from '../api';
import { defaultUserPicture } from '../imageUtils';

export default function Navbar({ token, user, setToken }) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setProfile(null);
      return;
    }

    fetch(`${API}/me`, { headers: { Authorization: token } })
      .then((res) => res.json())
      .then(setProfile)
      .catch(() => setProfile(null));
  }, [token]);

  const loadNotifications = () => {
    if (!token || !['buyer', 'farmer'].includes(user?.role)) {
      setNotifications([]);
      return;
    }

    fetch(`${API}/notifications`, { headers: { Authorization: token } })
      .then((res) => res.json())
      .then(setNotifications)
      .catch(() => setNotifications([]));
  };

  useEffect(() => {
    loadNotifications();
    if (!token || !['buyer', 'farmer'].includes(user?.role)) return undefined;

    const interval = setInterval(loadNotifications, 10000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setOpen(false);
    setNotificationsOpen(false);
    navigate('/');
  };

  const closeMenu = () => setOpen(false);
  const unreadCount = notifications.filter((notification) => !notification.read_at).length;
  const organizationRole = ['admin', 'buyer', 'farmer'].includes(user?.role);

  const markAllRead = async () => {
    await fetch(`${API}/notifications/read-all`, {
      method: 'POST',
      headers: { Authorization: token }
    });
    loadNotifications();
  };

  const openReceipt = async (notification) => {
    if (!notification.read_at) {
      await fetch(`${API}/notifications/${notification.id}/read`, {
        method: 'PATCH',
        headers: { Authorization: token }
      });
    }
    setNotificationsOpen(false);
    setOpen(false);
    navigate('/receipts');
    loadNotifications();
  };

  return (
    <header className="navbar">
      <div className="navTop">
        <Link className="brand" to={token ? '/dashboard' : '/'} onClick={closeMenu}>
          <img className="brandLogo" src={`${process.env.PUBLIC_URL}/assets/eyesOT_logo.png`} alt="" />
          <span>Digital Receipts</span>
        </Link>

        <button
          aria-expanded={open}
          aria-label="Toggle navigation menu"
          className="menuButton"
          onClick={() => setOpen((current) => !current)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <nav className={`navlinks ${open ? 'open' : ''}`}>
        {!token && <Link to="/" onClick={closeMenu}>Login</Link>}
        {token && <Link to="/dashboard" onClick={closeMenu}>Dashboard</Link>}
        {token && organizationRole && <Link to="/receipts" onClick={closeMenu}>Receipts</Link>}
        {token && organizationRole && <Link to="/product-data" onClick={closeMenu}>Product Data</Link>}
        {token && ['buyer', 'farmer'].includes(user?.role) && <Link to="/profile" onClick={closeMenu}>Profile</Link>}
        {token && user?.role === 'buyer' && <Link to="/create" onClick={closeMenu}>Create Receipt</Link>}
        {token && user?.role === 'admin' && <Link to="/admin" onClick={closeMenu}>Admin</Link>}
        {token && user?.role === 'admin' && <Link to="/admin/profiles" onClick={closeMenu}>Profiles</Link>}
        {token && user?.role === 'superadmin' && <Link to="/superadmin" onClick={closeMenu}>Organizations</Link>}
        <Link to="/about" onClick={closeMenu}>About</Link>
        {token && ['buyer', 'farmer'].includes(user?.role) && (
          <div className="notificationMenu">
            <button className="notificationButton" onClick={() => setNotificationsOpen((current) => !current)}>
              Notifications
              {unreadCount > 0 && <span className="notificationBadge">{unreadCount}</span>}
            </button>

            {notificationsOpen && (
              <div className="notificationDropdown">
                <div className="notificationHeader">
                  <strong>Notifications</strong>
                  {unreadCount > 0 && <button onClick={markAllRead}>Mark all read</button>}
                </div>
                {notifications.length === 0 && <p>No notifications.</p>}
                {notifications.map((notification) => (
                  <button
                    className={`notificationItem ${notification.read_at ? '' : 'unread'}`}
                    key={notification.id}
                    onClick={() => openReceipt(notification)}
                  >
                    <span>{notification.message}</span>
                    <small>{new Date(notification.created_at).toLocaleString()}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {token && (
          <button className="linkButton" onClick={logout}>
            Logout
          </button>
        )}

        {token && user && (
          <div className="navUser">
            <img className={`navAvatar ${profile?.blocked_at ? 'blockedAvatar' : ''}`} src={profile?.profile_picture || defaultUserPicture} alt="" />
            <span>{profile?.name || user.name} · {user.role}</span>
          </div>
        )}
      </nav>
    </header>
  );
}
